import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { hasArVersion, isArPath, toArPath, toEnPath, localizePath } from '../i18nRoutes';

/* ─── Multilingual dropdown labels ─── */
const menuLabels = {
  en: {
    home: 'Home', services: 'Services', tours: 'Tours', istanbul: 'Istanbul', investment: 'Investment',
    about: 'About', blog: 'Blog', contact: 'Contact',
    // Tours submenu
    privateTours: 'Private Istanbul Tours',
    oldCityTour: 'Istanbul Old City Tour',
    bosphorusCruises: 'Bosphorus Cruises',
    foodCulture: 'Food & Culture Tours',
    shoreExcursions: 'Shore Excursions',
    layoverTours: 'Layover Tours',
    toursFaq: 'Tours FAQ',
    // Istanbul submenu
    fatihOldCity: 'Fatih (Old City)',
    sultanahmet: 'Sultanahmet',
    beyoglu: 'Beyoğlu',
    karakoy: 'Karaköy',
    kadikoy: 'Kadıköy',
    galataport: 'Galataport',
    bosphorus: 'The Bosphorus',
    // Investment submenu
    citizenship: 'Turkish Citizenship',
    reConsulting: 'Real Estate Consulting',
    reListings: 'Real Estate Listings',
    buyingTips: 'Buying Tips (Istanbul)',
    // About submenu
    akinGuzel: 'Akın Güzel — Founder & Guide',
    faq: 'General FAQ',
    reviews: 'Reviews',
    contactUs: 'Contact Us',
  },
  ar: {
    home: 'الرئيسية', services: 'الخدمات', tours: 'الجولات', istanbul: 'إسطنبول', investment: 'الاستثمار',
    about: 'من نحن', blog: 'المدونة', contact: 'تواصل',
    privateTours: 'جولات إسطنبول الخاصة', oldCityTour: 'جولة المدينة القديمة',
    bosphorusCruises: 'رحلات البوسفور', foodCulture: 'جولات الطعام والثقافة',
    shoreExcursions: 'رحلات شاطئية', layoverTours: 'جولات الترانزيت', toursFaq: 'أسئلة الجولات',
    sultanahmet: 'السلطان أحمد', beyoglu: 'بيوغلو', karakoy: 'كاراكوي',
    kadikoy: 'قاضي كوي', galataport: 'غلطة بورت', bosphorus: 'البوسفور',
    fatihOldCity: 'الفاتح (المدينة القديمة)',
    citizenship: 'الجنسية التركية', reConsulting: 'استشارات عقارية', reListings: 'العقارات', buyingTips: 'نصائح شراء العقارات (إسطنبول)',
    akinGuzel: 'أكين غوزيل — المؤسس', faq: 'الأسئلة الشائعة', reviews: 'التقييمات', contactUs: 'تواصل معنا',
  },
  ru: {
    home: 'Главная', services: 'Услуги', tours: 'Туры', istanbul: 'Стамбул', investment: 'Инвестиции',
    about: 'О нас', blog: 'Блог', contact: 'Контакт',
    privateTours: 'Частные туры по Стамбулу', oldCityTour: 'Тур по Старому городу',
    bosphorusCruises: 'Круизы по Босфору', foodCulture: 'Гастрономические туры',
    shoreExcursions: 'Береговые экскурсии', layoverTours: 'Туры на пересадке', toursFaq: 'FAQ по турам',
    sultanahmet: 'Султанахмет', beyoglu: 'Бейоглу', karakoy: 'Каракёй',
    kadikoy: 'Кадыкёй', galataport: 'Галатапорт', bosphorus: 'Босфор',
    fatihOldCity: 'Фатих (Старый город)',
    citizenship: 'Турецкое гражданство', reConsulting: 'Консультации по недвижимости', reListings: 'Недвижимость', buyingTips: 'Советы покупателям (Стамбул)',
    akinGuzel: 'Акын Гюзель — Основатель', faq: 'Общие FAQ', reviews: 'Отзывы', contactUs: 'Связаться',
  },
  tr: {
    home: 'Ana Sayfa', services: 'Hizmetler', tours: 'Turlar', istanbul: 'İstanbul', investment: 'Yatırım',
    about: 'Hakkımızda', blog: 'Blog', contact: 'İletişim',
    privateTours: 'Özel İstanbul Turları', oldCityTour: 'Tarihi Yarımada Turu',
    bosphorusCruises: 'Boğaz Turları', foodCulture: 'Yemek & Kültür Turları',
    shoreExcursions: 'Kıyı Gezileri', layoverTours: 'Aktarma Turları', toursFaq: 'Tur SSS',
    sultanahmet: 'Sultanahmet', beyoglu: 'Beyoğlu', karakoy: 'Karaköy',
    kadikoy: 'Kadıköy', galataport: 'Galataport', bosphorus: 'Boğaziçi',
    fatihOldCity: 'Fatih (Tarihi Yarımada)',
    citizenship: 'Türk Vatandaşlığı', reConsulting: 'Emlak Danışmanlığı', reListings: 'Emlak Listesi', buyingTips: 'Satın Alma İpuçları (İstanbul)',
    akinGuzel: 'Akın Güzel — Kurucu', faq: 'Genel SSS', reviews: 'Yorumlar', contactUs: 'Bize Ulaşın',
  },
};

export function StickyNav({ language, setLanguage, t }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [hoverMenu, setHoverMenu] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const navRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isRtl = language === 'ar';
  const ml = menuLabels[language] || menuLabels.en;
  const loc = (p) => localizePath(p, language);

  const changeLanguage = (l) => {
    const cur = location.pathname;
    if (l === 'ar') {
      setLanguage('ar');
      const en = toEnPath(cur);
      if (!isArPath(cur)) navigate(hasArVersion(en) ? toArPath(en) : '/ar');
    } else {
      setLanguage(l);
      if (isArPath(cur)) navigate(toEnPath(cur));
    }
  };

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close everything on navigation
  useEffect(() => { setMobileOpen(false); setOpenMobileDropdown(null); setHoverMenu(null); }, [location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => () => clearTimeout(hoverTimeoutRef.current), []);

  // Click outside to close
  const handleOutside = useCallback((e) => {
    if (navRef.current && !navRef.current.contains(e.target)) {
      setOpenMobileDropdown(null);
      setMobileOpen(false);
      setHoverMenu(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [handleOutside]);

  const closeAll = () => { setOpenMobileDropdown(null); setMobileOpen(false); setHoverMenu(null); };

  // Desktop hover intent — open immediately, close after 250ms delay
  const handleMouseEnter = (id) => {
    clearTimeout(hoverTimeoutRef.current);
    setHoverMenu(id);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverMenu(null);
    }, 250);
  };

  // Mobile accordion toggle
  const toggleMobileDropdown = (id) => {
    setOpenMobileDropdown(prev => prev === id ? null : id);
  };

  // Keyboard: Enter/Space toggles dropdown, Escape closes
  const handleDropdownKeyDown = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      toggleMobileDropdown(id);
    } else if (e.key === 'Escape') {
      setOpenMobileDropdown(null);
    }
  };

  const langLabels = { en: 'EN', ru: 'RU', ar: '\u0639\u0631\u0628\u064A', tr: 'TR' };

  // Menu structure
  const dropdowns = [
    {
      id: 'tours',
      label: ml.tours,
      matchPaths: ['/travel', '/tours', '/private-istanbul-tours', '/istanbul-old-city-tour', '/istanbul-tours-faq', '/layover-tours-istanbul'],
      items: [
        { to: '/private-istanbul-tours', label: ml.privateTours },
        { to: '/istanbul-old-city-tour', label: ml.oldCityTour },
        { to: '/tours/bosphorus-cruises', label: ml.bosphorusCruises },
        { to: '/tours/food-culture-tours', label: ml.foodCulture },
        { to: '/tours/galataport-shore-excursions', label: ml.shoreExcursions },
        { to: '/layover-tours-istanbul', label: ml.layoverTours },
        { to: '/istanbul-tours-faq', label: ml.toursFaq },
      ]
    },
    {
      id: 'istanbul',
      label: ml.istanbul,
      matchPaths: ['/istanbul'],
      items: [
        { to: '/istanbul/fatih-old-city', label: ml.fatihOldCity },
        { to: '/istanbul/sultanahmet', label: ml.sultanahmet },
        { to: '/istanbul/beyoglu', label: ml.beyoglu },
        { to: '/istanbul/karakoy', label: ml.karakoy },
        { to: '/istanbul/kadikoy', label: ml.kadikoy },
        { to: '/istanbul/galataport', label: ml.galataport },
        { to: '/istanbul/bosphorus', label: ml.bosphorus },
      ]
    },
    {
      id: 'investment',
      label: ml.investment,
      matchPaths: ['/real-estate', '/tours/turkish-citizenship', '/tours/real-estate'],
      items: [
        { to: '/tours/turkish-citizenship-investment', label: ml.citizenship },
        { to: '/tours/real-estate-consulting', label: ml.reConsulting },
        { to: '/real-estate', label: ml.reListings },
        { to: '/real-estate/tips-buying-real-estate-istanbul', label: ml.buyingTips },
      ]
    },
    {
      id: 'about',
      label: ml.about,
      matchPaths: ['/about', '/faq', '/reviews'],
      items: [
        { to: '/about/akin-guzel', label: ml.akinGuzel },
        { to: '/faq', label: ml.faq },
        { to: '/reviews', label: ml.reviews },
        { to: '/contact', label: ml.contactUs },
      ]
    },
  ];

  const isActiveParent = (dd) => dd.matchPaths.some(p => location.pathname.startsWith(p));

  return (
    <nav className={`sticky-nav ${scrolled ? 'scrolled' : ''}`} ref={navRef} dir={isRtl ? 'rtl' : 'ltr'} data-testid="sticky-nav" role="navigation" aria-label="Main navigation">
      <div className="sn-inner">
        <Link to={loc('/')} className="sn-logo" onClick={closeAll} data-testid="sn-logo">
          <img src="/images/viaturkey-logo.jpg" alt="Viaturkey" />
          <span>Viaturkey</span>
        </Link>

        <button
          className={`sn-hamburger ${mobileOpen ? 'open' : ''}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="sn-hamburger"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>

        <div className={`sn-links ${mobileOpen ? 'show' : ''}`} data-testid="sn-links" role="menubar">
          {/* HOME — no dropdown */}
          <Link to={loc('/')} className={`sn-link ${location.pathname === '/' || location.pathname === '/ar' ? 'active' : ''}`} onClick={closeAll} data-testid="sn-home" role="menuitem">
            {ml.home}
          </Link>

          {/* SERVICES — no dropdown */}
          <Link to="/services" className={`sn-link ${location.pathname === '/services' ? 'active' : ''}`} onClick={closeAll} data-testid="sn-services" role="menuitem">
            {ml.services}
          </Link>

          {/* DROPDOWN MENUS */}
          {dropdowns.map(dd => (
            <div
              key={dd.id}
              className={`sn-dropdown ${openMobileDropdown === dd.id ? 'mobile-open' : ''} ${hoverMenu === dd.id ? 'hover-open' : ''}`}
              data-testid={`sn-dropdown-${dd.id}`}
              role="none"
              onMouseEnter={() => handleMouseEnter(dd.id)}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`sn-link sn-dd-trigger ${isActiveParent(dd) ? 'active' : ''}`}
                onClick={() => toggleMobileDropdown(dd.id)}
                onKeyDown={(e) => handleDropdownKeyDown(e, dd.id)}
                data-testid={`sn-${dd.id}-trigger`}
                aria-expanded={openMobileDropdown === dd.id || hoverMenu === dd.id}
                aria-haspopup="true"
                role="menuitem"
              >
                {dd.label} <i className="fas fa-chevron-down sn-arrow"></i>
              </button>
              <div className="sn-dd-menu" role="menu" aria-label={dd.label}>
                {dd.items.map(item => (
                  <Link key={item.to} to={loc(item.to)} onClick={closeAll} role="menuitem" data-testid={`sn-link-${item.to.replace(/\//g, '-').slice(1)}`}>
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* BLOG — no dropdown */}
          <Link to={loc('/blog')} className={`sn-link ${location.pathname.includes('/blog') ? 'active' : ''}`} onClick={closeAll} data-testid="sn-blog" role="menuitem">
            {ml.blog}
          </Link>

          {/* CONTACT — no dropdown */}
          <Link to={loc('/contact')} className={`sn-link sn-contact-link ${location.pathname.endsWith('/contact') ? 'active' : ''}`} onClick={closeAll} data-testid="sn-contact" role="menuitem">
            <i className="fas fa-headset"></i> {ml.contact}
          </Link>

          {/* LANGUAGE SWITCHER */}
          <div className="sn-lang-group" role="group" aria-label="Language">
            {['en', 'ru', 'ar', 'tr'].map(l => (
              <button key={l} className={`sn-lang-btn ${language === l ? 'active' : ''}`} onClick={() => { changeLanguage(l); closeAll(); }} data-testid={`sn-lang-${l}`}>
                {langLabels[l]}
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}
